import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Blank means "unknown" rather than zero, so an empty field must survive as null.
const toNumberOrNull = (v) => {
  const s = String(v ?? '').trim();
  if (s === '') return null;
  const n = Number(s.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const fmtPct = (v) => (v == null ? null : `0–${v}%`);
const fmtUsd = (v) => (v == null ? null : `$0–$${v.toLocaleString('en-US')}`);

function LevelBandsEditor({ bands, onSave, onReset }) {
  const { t } = useTranslation();
  const [editingLevel, setEditingLevel] = useState(null);
  const [draft, setDraft] = useState({ cash: '', stock: '' });
  const [newRow, setNewRow] = useState({ level: '', cash: '', stock: '' });

  const startEdit = (band) => {
    setEditingLevel(band.level);
    setDraft({
      cash: band.cashBonusMaxPercent ?? '',
      stock: band.stockAwardMaxUSD ?? ''
    });
  };

  const commit = async (level) => {
    await onSave(level, {
      cashBonusMaxPercent: toNumberOrNull(draft.cash),
      stockAwardMaxUSD: toNumberOrNull(draft.stock)
    });
    setEditingLevel(null);
  };

  const addRow = async () => {
    const level = parseInt(newRow.level, 10);
    if (!Number.isInteger(level) || level < 50 || level > 80) return;
    await onSave(level, {
      cashBonusMaxPercent: toNumberOrNull(newRow.cash),
      stockAwardMaxUSD: toNumberOrNull(newRow.stock)
    });
    setNewRow({ level: '', cash: '', stock: '' });
  };

  const unset = <span className="level-range-unset">{t('compensation.unknown')}</span>;

  return (
    <div className="comp-list-table">
      <table className="comp-table">
        <thead>
          <tr>
            <th>{t('compensation.level')}</th>
            <th>{t('compensation.cashMaxPercent')}</th>
            <th>{t('compensation.stockMaxUSD')}</th>
            <th>{t('compensation.onTarget')}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {bands.map(band => {
            const isEditing = editingLevel === band.level;
            // On target is half the range, because a 100 rating pays half of a 0-200 scale.
            const onTargetCash = band.cashBonusMaxPercent != null
              ? `${band.cashBonusMaxPercent / 2}%` : null;
            const onTargetStock = band.stockAwardMaxUSD != null
              ? `$${(band.stockAwardMaxUSD / 2).toLocaleString('en-US')}` : null;

            return (
              <tr key={band.level}>
                <td><span className="level-badge">L{band.level}</span></td>
                <td>
                  {isEditing ? (
                    <input
                      type="number"
                      step="0.5"
                      value={draft.cash}
                      onChange={e => setDraft(d => ({ ...d, cash: e.target.value }))}
                      aria-label={t('compensation.cashMaxPercent')}
                    />
                  ) : (fmtPct(band.cashBonusMaxPercent) ?? unset)}
                </td>
                <td>
                  {isEditing ? (
                    <input
                      type="number"
                      step="500"
                      value={draft.stock}
                      onChange={e => setDraft(d => ({ ...d, stock: e.target.value }))}
                      aria-label={t('compensation.stockMaxUSD')}
                    />
                  ) : (fmtUsd(band.stockAwardMaxUSD) ?? unset)}
                </td>
                <td>
                  {[onTargetCash, onTargetStock].filter(Boolean).join(' · ') || unset}
                  <div className="level-range-source">
                    {band.isCustom ? t('compensation.custom') : t('compensation.builtIn')}
                  </div>
                </td>
                <td className="actions-cell">
                  {isEditing ? (
                    <>
                      <button className="btn-icon" onClick={() => commit(band.level)} aria-label="Save">✅</button>
                      <button className="btn-icon" onClick={() => setEditingLevel(null)} aria-label="Cancel">✖️</button>
                    </>
                  ) : (
                    <>
                      <button className="btn-icon" onClick={() => startEdit(band)} aria-label="Edit level range">✏️</button>
                      {band.isCustom && (
                        <button
                          className="btn-icon"
                          onClick={() => onReset(band.level)}
                          aria-label={t('compensation.resetToDefault')}
                          title={t('compensation.resetToDefault')}
                        >↩️</button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            );
          })}

          <tr>
            <td>
              <input
                type="number"
                min="50"
                max="80"
                placeholder="66"
                value={newRow.level}
                onChange={e => setNewRow(r => ({ ...r, level: e.target.value }))}
                aria-label={t('compensation.level')}
              />
            </td>
            <td>
              <input
                type="number"
                step="0.5"
                value={newRow.cash}
                onChange={e => setNewRow(r => ({ ...r, cash: e.target.value }))}
                aria-label={t('compensation.cashMaxPercent')}
              />
            </td>
            <td>
              <input
                type="number"
                step="500"
                value={newRow.stock}
                onChange={e => setNewRow(r => ({ ...r, stock: e.target.value }))}
                aria-label={t('compensation.stockMaxUSD')}
              />
            </td>
            <td colSpan="2">
              <button className="btn-text" onClick={addRow}>+ {t('compensation.addLevelRange')}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default LevelBandsEditor;
